import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import Dropdown from "components/ui/dropdown";
import Switch from "components/ui/switch";

import { saveSettings } from "store/actions/auth/auth.actions";
import { getPendingBets, setRigthbarTab, setUseQuickBet } from "store/actions/betslip/betslip.actions";

import { mergeClassNames } from "utils/common";
import { getCurrentSettings } from "utils/settings";

import { SWITCH_SIZES } from "constants/ui.constants";
import { QUICK_BET_ANIMATABLE_EL_DATA_ATTRS } from "constants/betslip.constants";

import historyType from "types/history.type";
import sessionType from "types/session.type";

const TABS = {
	BETSLIP: 1,
	PENDING_BETS: 2
};

const RightBarTabs = ({ setRigthbarTab, rightbarTab, pendings, getPendingBets, saveSettings, session, setUseQuickBet }) => {
	const { t } = useTranslation();

	const isQuickBetEnabled = getCurrentSettings(session).isQuickBet;
	const [quickBet, setQuickBet] = useState(isQuickBetEnabled);
	const [fadeClass, setFadeClass] = useState("");
	const [showDropdownContent, setShowDropdownContent] = useState(false);

	useEffect(() => {
		if (quickBet !== isQuickBetEnabled) {
			setFadeClass("fade-out");
			const timer = setTimeout(() => {
				setQuickBet(isQuickBetEnabled);
				setFadeClass("fade-in");
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [isQuickBetEnabled, quickBet]);

	const toggleQuickBet = (value) => {
		saveSettings({ isQuickBet: value });
	};

	useEffect(() => {
		setUseQuickBet(isQuickBetEnabled);
	}, [isQuickBetEnabled]);

	const renderDropdownContent = () => {
		const onItemClick = (tab) => {
			setRigthbarTab(tab);
			setShowDropdownContent(false);
		}

		return (
			<div className="vs--betslip-dropdown-content vs--flex vs--flex-col">
				<div
					className={mergeClassNames("vs--flex vs--align-center vs--cursor-pointer vs--pl-8 vs--pr-8", "vs--betslip-dropdown-content-item", rightbarTab === TABS.BETSLIP && "vs--title-brand")}
					onClick={() => onItemClick(TABS.BETSLIP)}
				>
					{t("bet.betslip")}
				</div>
				<div
					className={mergeClassNames("vs--flex vs--align-center vs--cursor-pointer vs--pl-8 vs--pr-8", "vs--betslip-dropdown-content-item", rightbarTab === TABS.PENDING_BETS && "vs--title-brand")}
					onClick={() => onItemClick(TABS.PENDING_BETS)}
				>
					{t("bet.pendingBets")}
					{pendings.length > 0 && <b className="vs--badge vs--title-black vs--font-smallest vs--font-bold vs--ml-6">{pendings.length}</b>}
				</div>
			</div>
		);
	};

	useEffect(() => {
		getPendingBets();
	}, [session.isDemo]);

	return (
		<div className="vs--flex vs--mb-2">
			<div className={mergeClassNames("vs--betslip-header vs--text-center vs--flex vs--align-center vs--justify-between vs--pt-8 vs--pb-8 vs--pl-12 vs--pr-12", rightbarTab === TABS.BETSLIP && "vs--betslip-section-active")} data-unique={rightbarTab === TABS.BETSLIP ? "betslip" : "pendingBets"}>
				<Dropdown
					data-element-id={QUICK_BET_ANIMATABLE_EL_DATA_ATTRS.BETSLIP_HEADER}
					showDropdownContent={showDropdownContent}
					setShowDropdownContent={setShowDropdownContent}
					dropdownContent={renderDropdownContent()} placement="bottom-start"
				>
					<div className="vs--flex vs--align-center vs--cursor-pointer">
						<i className={mergeClassNames("vs--title-brand vs--font-bigest", fadeClass, quickBet ? "ic_thunder" : "ic_quick-betslip")} />
						<span className="vs--title-white vs--font-normal vs--font-regular vs--ml-4">{t(`bet.${rightbarTab === TABS.BETSLIP ? "betslip" : "pendingBets"}`)}</span>
						<i className="ic_right vs--cursor-pointer vs--font-bigest" />
					</div>
				</Dropdown>
				<div className="vs--betslip-header-quick-bet vs--flex vs--align-center">
					<span className="vs--betslip-header-quick-bet-title vs--title-gray-80">{t("bet.quickBet")}</span>
					<Switch className="vs--betslip-header-quick-bet-switch vs--ml-8" size={SWITCH_SIZES.LARGE} indicatorContent={<i className={mergeClassNames("ic_thunder", quickBet && "vs--title-brand")} />} checked={isQuickBetEnabled} onChange={toggleQuickBet} />
				</div>
			</div>
		</div>
	);
};

/** RightBarTabs propTypes
 * PropTypes
 */
RightBarTabs.propTypes = {
	/** Redux action to set active rightbar tab(betslip/pendings) */
	setRigthbarTab: PropTypes.func,
	/** Redux state property, selected tab(betslip/pendings) */
	rightbarTab: PropTypes.oneOf([TABS.BETSLIP, TABS.PENDING_BETS]),
	/** Redux state property, pending bets */
	pendings: PropTypes.arrayOf(historyType),
	/** Redux action to get pending bets */
	getPendingBets: PropTypes.func,
	/** Redux state property, current session */
	session: sessionType,
	/** Redux action to switch player to the real mode */
	saveSettings: PropTypes.func,
	/** Redux action to set quick bet data */
	setUseQuickBet: PropTypes.func
};


const mapDispatchToProps = (dispatch) => ({
	getPendingBets: () => {
		dispatch(getPendingBets());
	},
	setRigthbarTab: (tab) => {
		dispatch(setRigthbarTab(tab));
	},
	saveSettings: (data) => {
		dispatch(saveSettings(data));
	},
	setUseQuickBet: (enabled) => {
		dispatch(setUseQuickBet(enabled));
	}
});

const mapStateToProps = (state) => {
	return {
		pendings: state.betslip.pendings,
		rightbarTab: state.betslip.rightbarTab,
		session: state.auth.session
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(RightBarTabs);
